import Box from "@mui/material/Box";
import { gr } from "src/design-system/tokens";
import icons from "src/design-system/icons";
import GrPageHeader from "src/design-system/components/GrPageHeader";
import GrButton from "src/design-system/components/GrButton";
import GrIconButton from "src/design-system/components/GrIconButton";
import GrIcon from "src/design-system/components/GrIcon";
import GrBadge from "src/design-system/components/GrBadge";
import GrLoader from "src/design-system/components/GrLoader";

/**
 * Top of a programme's calendar (GeneralCalendar `renderHeader`): back arrow, programme title with
 * its difficulty level, Details (the programme's detail page) and My Compliance (harmony ring
 * programmes), and the month controls
 * (‹ month ›, Expand all / Collapse all, Today). The calendar under it is unchanged.
 */

const toolbarSx = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "space-between",
  gap: gr.space[3],
  pt: gr.space[4],
  borderTop: `1px solid ${gr.color.line}`,
};
const monthNavSx = { display: "flex", alignItems: "center", gap: gr.space[2] };
const monthSx = {
  m: 0,
  minWidth: `calc(${gr.space[12]} * 2.5)`,
  textAlign: "center",
  fontFamily: gr.font.family,
  fontSize: gr.font.size.xl,
  fontWeight: gr.font.weight.medium,
  color: gr.color.brandStrong,
};
const actionsSx = { display: "flex", flexWrap: "wrap", alignItems: "center", gap: gr.space[2] };

export default function ProgrammeCalendarHeader({
  title,
  level,
  onBack,
  onDetails,
  onCompliance,
  monthLabel,
  onPrev,
  onNext,
  loading,
  showArrows = true,
  canExpand,
  onExpandAll,
  onCollapseAll,
  onToday,
}) {
  return (
    <Box sx={{ mb: gr.space[4] }}>
      <GrPageHeader
        sx={{ mb: gr.space[5] }}
        onBack={onBack}
        title={title || "Programme"}
        trailing={level ? <GrBadge tone="brand">{level} level</GrBadge> : null}
        actions={
          onDetails || onCompliance ? (
            <>
              {onDetails ? (
                <GrButton
                  variant="secondary"
                  startIcon={<GrIcon icon={icons.info} size="md" />}
                  onClick={onDetails}
                >
                  Details
                </GrButton>
              ) : null}
              {onCompliance ? (
                <GrButton variant="secondary" onClick={onCompliance}>
                  My Compliance
                </GrButton>
              ) : null}
            </>
          ) : null
        }
      />

      <Box sx={toolbarSx}>
        <Box sx={monthNavSx}>
          {showArrows ? (
            <GrIconButton aria-label="Previous month" onClick={onPrev}>
              <GrIcon icon={icons.chevronLeft} />
            </GrIconButton>
          ) : null}
          <Box component="h2" aria-live="polite" sx={monthSx}>
            {monthLabel}
          </Box>
          {showArrows ? (
            <GrIconButton aria-label="Next month" onClick={onNext}>
              <GrIcon icon={icons.chevronRight} />
            </GrIconButton>
          ) : null}
          {loading ? <GrLoader inline size="sm" label="Loading the month" /> : null}
        </Box>

        <Box sx={actionsSx}>
          {canExpand ? (
            <>
              <GrButton variant="quiet" size="sm" onClick={onExpandAll} startIcon={<GrIcon icon={icons.chevronDown} />}>
                Expand all
              </GrButton>
              <GrButton variant="quiet" size="sm" onClick={onCollapseAll} startIcon={<GrIcon icon={icons.chevronUp} />}>
                Collapse all
              </GrButton>
            </>
          ) : null}
          <GrButton variant="secondary" size="sm" onClick={onToday}>
            Today
          </GrButton>
        </Box>
      </Box>
    </Box>
  );
}
